export const severityCodes = {
  0: 'X',
  1: 'L',
  2: 'M',
  3: 'H'
};

export const distressInfo = {
  ravel: {
    name: 'Ravel',
    descriptionBasic: "Raveling is the dislodging of coarse aggregate particles, usually via mechanical means (traffic loading)",
    impact: "Loose debris on the pavement, increased roughness (an indicator of structural failure); water collecting in the raveled locations, resulting in vehicle hydroplaning; loss of skid resistance",
    possibleCauses: "Ravelling may be caused by loss of bond between aggregate and asphalt binder (e.g. insufficient asphalt binder, poor mixture quality, insufficient compaction, segregation, or stripping), or mechanical dislodging of aggregate by certain traffic (e.g. studded tires, snowplow blades, or tracked vehicled).",
    descriptionDetailed: "In general, ravelling features a loss of bond between aggregate particles and the asphalt binder. There are several pathways for this to occur: (1) As asphalt binder ages naturally (binder oxidation), oxygen reacts with its constituent molecules, resulting in a stiffer and more viscous material that is more likely to lose aggregate dislodged by traffic. (2) Asphalt binder bonds with dust coating aggregate particles, rather than the aggregate themselves. (3) Aggregates are improperly distributed (segregated) or areas are missing fine particles in the aggregate matrix, leaving less aggregate surface area for the asphalt binder. (4) Inadequate compaction during construction can reduce cohesion between aggregates and HMA and results in inadequate pavement density.",
    selectionGuidance: [
      "Raveling is not a structural failure. If it is the only visible failure, then resurfacing is most appropriate.",
      "It is difficult to determine the severity of raveling in a given area. For this guide, severity is judged based on the area of raveling and not the amount of dislodged aggregate."
    ],
    descriptionSeverity: [
      "L: Isolated areas, usually in wheelpaths",
      "M: Majority of wheelpaths affected",
      "H: Most of the lane is affected"
    ]
  },
  bleedFlush: {
    name: 'Bleed/Flush',
    descriptionBasic: "Bleeding (or flushing) is a film of bituminous material on the pavement surface that creates a shiny, glasslike reflecting surface that usually becomes quite sticky.",
    impact: "Bleeding (or flushing) results in loss of skid resistance when wet and an unsightly pavement.",
    possibleCauses: "Bleeding (or flushing) is caused by excessive amounts of asphaltic cement or tars in the mix, excess application of a bimtuminous sealant, or low air void content, or a combination thereof.",
    descriptionDetailed: "Bleeding occurs when asphalt binder fills the aggregate voids during hot weather or traffic compaction, and then expands onto the pavement surface. Since bleeding is not reversible during cold weather or periods of low loading, asphalt binder will accumulate on the pavement surface over time",
    selectionGuidance: [
      "* It is difficult to determine the amount of bleeding/flushing in a particular area from a moving vehicle. For this guide, severity is judged based on the area of bleeding/flushing and not the volume of asphalt binder showing on the pavement surface.",
      "Bleeding/flushing should be removed via milling if possible before resurfacing."
    ],
    descriptionSeverity: [
      "L: Patchy areas, usually in wheelpaths ",
      "M: Majority of wheelpaths affected ",
      "H: Most of the lane is affected"
    ]
  },
  rutting: {
    name: 'Rutting',
    descriptionBasic: "A rut is a surface depression in the wheelpath, which may also include the occurance of pavement uplift, or shearing along the sides of a rut. Ruts are particularly evident when filled with water, such as after rainfall.",
    impact: "Ruts can cause vehicle hydroplaning when filled with water; ruts tend to pull vehicles towards ruts as vehicles attempt to steer across them, creating hazardous driving conditions.",
    possibleCauses: "Rutting may be attributed to insufficient compaction of HMA layers during construction, causing the pavement to conintuously densify under live traffic. Other causes include improper mix design (excess asphalt content, excessive mineral filler, inadequate amount of angular aggregate), or subgrade rutting from insufficient pavement structure. Rutting can also be caused by studded tires, but such ruts are caused by mechanical means rather than pavement deformations caused mix design or subgrade issues.",
    descriptionDetailed: "Permanent deformations in pavement layers or subgrade are caused by consolidation issues or traffic loading causing lateral movement of the materials.  Ruts are one such deformation in which a surface depression in the wheelpath may also include the occurance of pavement uplift, or shearing along the sides of a rut. " +
      "This is the result of two kinds of rutting: (1) mix rutting, where only layers above the subhrade exhibit wheelpath depressions from compaction or mix design problems; and (2) subgrade rutting, where the subgrade exhibits wheelpath depressions from loading and causes the above pavement layers to settle into the depression.",
    selectionGuidance: [
      "Rut depth should be measured from the deepest part of the rut to the normal pavement surface. This DOES NOT include the raised lip on one or both sides of the rut often caused by plastic deformation of the pavement surface. "
    ],
    descriptionSeverity: [
      "L: Rut depth ≤ 1/4\"",
      "M: Rut depth 1/4\" to 3/4\"",
      "H: Rut depth > 3/4\"",
    ]
  },
  patching: {
    name: 'Patching',
    descriptionBasic: "Also known as Asphalt Patching, a patch is an area of pavement that has been replaced with new material to repair the existing pavement. A patch is considered a defect no matter how well it is perfomring.",
    impact: "Patches increases roughness (an indicator of structural failure); unsealed edges of a patch can lead to water infilitration into the subgrade; unsightly.",
    possibleCauses: "Patching is caused either by utility cuts or previous localized pavement repair activities (i.e. a patch of deteriorated pavement that has been removed/patched). ",
    descriptionDetailed: "Patches are the result of repair activities and the apparent differences between the old and new materials. The edges of patched areas can become separated from the surrounding pavement, and if left unsealed, can lead to water infilitration into the sugrade.",
    selectionGuidance: [
      "For the purposes of this guide patching is considered to be an inferior pavement surface done to lesser quality than a typical resurfacing. Therefore, it is assumed to have a shorter life. Also, patching can be an indication of an underlying uncorrected failure like base or subgrade failure.  "
    ],
    descriptionSeverity: [
      "L: ≤ 5% of surface area",
      "M: 6% to 25% of surface area",
      "H: > 25% of surface area"
    ]
  },
  alligatorCracking: {
    name: 'Alligator Cracking',
    descriptionBasic: "A series of interconnected cracks caused by fatigue failure of the HMA surface under repeated traffic loading. These longitudinal cracks connect, forming many-sided sharp-angled pieces that develop into a pattern resembling the back of an alligator or crocodile.",
    impact: "Longitudinal cracks connect forming many-sided sharp-angled pieces that develop into a pattern resembling the back of an alligator or crocodile. This eventually results in potholes and pavement disintegration.",
    possibleCauses: "Alligator cracking is caused by inadequate structural support for given traffic loading conditions. For example, this could be attributed to a loss of base, subbase or subgrade support from poor drainage; stripping on the bottom an HMA layer; unanticipated increases in traffic loading, inadequate design thickness for anticipated loads, or inadequate compaction.",
    descriptionDetailed: "Roughness (an indicator of structural failure) cracks allow moisture infiltration into the baseand subgrade. As the number and magnitude of loads becomes too great, longitudinal cracks begin to form (usually in the wheelpaths). After repeated loading, these longitudinal cracks connect forming many-sided sharp-angled pieces that develop into a pattern resembling the back of an alligator or crocodile.",
    selectionGuidance: [
      "Alligator cracking is an indicator of structural failure (loads are greater than can be supported by the pavement structure), advanced cracking of another type (e.g., longitudinal cracking), or subgrade failure.",
      "Before selecting a treatment type, investigate the underlying cause of alligator cracking. If the base/subgrade is weak and deforming, a full-depth repair of the area is warranted rather than just a resurfacing."
    ],
    descriptionSeverity: [
      "L: Hairline",
      "M: Spalling",
      "H: Spalling & Pumping",
    ]
  },
  blockCracking: {
    name: 'Block Cracking',
    descriptionBasic: "Block cracks are interconnected cracks that divide the pavement into approximately rectangular pieces. The blocks may range in size from approximately 0.3 by 0.3 m (1x1 ft) to 3 by 3 m (10x10 ft). The block cracking normally occurs over a large portion of the pavement area, but sometimes will poccur only in non-traffic areas.",
    impact: "Block cracking allows moisture infilitration into the pavement and increases the roughness (an in indicator of structural failure).",
    possibleCauses: "Block cracking is caused mainly by shrinkage of the asphalt concrete and daily temperature cycling, which results in faily stress/strain cycling.",
    descriptionDetailed: "The inability of the asphalt binder to expand and contract with the daily temperature cycles may be a result of asphalt binder aging or poor choice of asphalt binder in the mix. Notably, block cracking is not load-associated.",
    selectionGuidance: [
      "Block cracking is indicative of surface aging and is NOT load related. A resurfacing should correct the issue unless it is severe."
],
    descriptionSeverity: [
      "L: Cracks ≤ 1/4\" wide ",
      "M: Cracks over 1/4\" with no spalling",
      "H: Cracks > 1/4\" with spalling",
    ]
  },
  transverseCracking: {
    name: 'Transverse Cracking',
    descriptionBasic: "Transverse cracks are cracks that form perpendicular to the pavement centerline (or directions of travel) , usually a type of thermal cracking.",
    impact: "Transverse cracks increase roughness (an indicator of structural failure); cracks also allow moisture infilitration into the subgrade",
    possibleCauses: "Possible causes may include (1) HMA surface shrinkage due to low temperature or asphalt binder stiffening, (2) reflective cracks propagating from cracks beneath the surface HMA layer, or (3) top-down cracking",
    descriptionDetailed: "Transverse cracks can be distinguished based on the extent and severity of cracking. Generally, low severity refers to cracks < 1/2 inch wide and few in localized areas; high severity refers to cracks wider than 1/2 inch and pervasive throughout a segment. In RxPave, extent to the frequency of cracks per 100 feet of pavement.",
    selectionGuidance: [
      "Transverse cracking is the result of pavement volume contraction in cold weather. Usually they occur at regular intervals associated with the amount of contraction."
],
    descriptionSeverity: [
      "L: 1 to 4 cracks per 100\'",
      "M: 5 to 9 cracks per 100\'",
      "H: 10 or more cracks per 100\'"
    ]
  },
  longitudinalCracking:{
    name: 'Longitudinal Cracking',
    descriptionBasic:"Longitudinal cracks are a type of fatigue cracking that features cracks parallel to the pavement centerline (or directions of travel) and generally divide the slab into two or three pieces.",
    impact: "Longitudinal cracking allows moisture infiltration, increases roughness (an indicator of structural failure), and indicates possible onset of fatigue cracking and structural failure. ",
    possibleCauses: "These cracks are usually are caused by a combination of repeated traffic loading, thermal gradient curling, and repeated moisture loading. There are several common causes, including: (1) poor joint construction or location, such as inside the wheelpath where loading is more frequent; (2) a reflective crack from an underlying layer; (3) HMA fatigue; and (4) top-down cracking",
    descriptionDetailed: "Longitudinal cracks can be distinguished based on the extent and severity of cracking. Generally, low severity refers to cracks < 1/2 inch wide and few in localized areas; high severity refers to cracks wider than 1/2 inch and pervasive throughout a segment. In RxPave, extent refers to the sum of apparent longitudinal crack lengths relative to the segment length.",
    selectionGuidance: [
      "Can be the result of poor longitudinal joint construction or top-down cracking.",
      "Poor joint construction: Cracks are usually very straight and occur on joint locations, often on lane markers.",
      "Top-down cracking: Usually occurs on pavements with 5 inches or greater of HMA. Cracks are not as straight and can progress to alligator cracking."
    ],
    descriptionSeverity: [
      "L: 1% to 99% of segment length",
      "M: 100% to 199% of segment length",
      "H: 200% or more of segment length"
    ]
  }
};


/*
The point map structure is:

    renewalOptionPointMap = {
      distressType: {
        0: notPresentPoints,
        1: lowSeverityPoints,
        2: medSeverityPoints,
        3: highSeverityPoints
      }
    }

The number of points are mapped as follows:
  Recommended: 2
  Conditional: 1
  Not recommended: -1
*/

export const chipCapePointMap = {
  ravel: {
    0: 0,
    1: 2,
    2: 2,
    3: 1,
  },
  bleedFlush: {
    0: 0,
    1: 1,
    2: 1,
    3: -1,
  },
  rutting: {
    0: 0,
    1: 1,
    2: -1,
    3: -1,
  },
  patching: {
    0: 0,
    1: 1,
    2: 1,
    3: -1,
  },
  alligatorCracking: {
    0: 0,
    1: 2,
    2: 1,
    3: -1,
  },
  blockCracking: {
    0: 0,
    1: 2,
    2: 1,
    3: 1,
  },
  transverseCracking: {
    0: 0,
    1: 2,
    2: 1,
    3: 1,
  },
  longitudinalCracking: {
    0: 0,
    1: 2,
    2: 1,
    3: 1,
  }
};

export const thinHMAPointMap = {
  ravel: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  },
  bleedFlush: {
    0: 0,
    1: 1,
    2: 1,
    3: -1,
  },
  rutting: {
    0: 0,
    1: 2,
    2: 1,
    3: -1,
  },
  patching: {
    0: 0,
    1: 1,
    2: 1,
    3: -1,
  },
  alligatorCracking: {
    0: 0,
    1: 2,
    2: 2,
    3: -1,
  },
  blockCracking: {
    0: 0,
    1: 1,
    2: 2,
    3: 1,
  },
  transverseCracking: {
    0: 0,
    1: 1,
    2: 2,
    3: 1,
  },
  longitudinalCracking: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  }
};

export const fullDepthPointMap = {
  ravel: {
    0: 0,
    1: -1,
    2: -1,
    3: -1,
  },
  bleedFlush: {
    0: 0,
    1: -1,
    2: 1,
    3: 2,
  },
  rutting: {
    0: 0,
    1: -1,
    2: 2,
    3: 2,
  },
  patching: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  },
  alligatorCracking: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  },
  blockCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  },
  transverseCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  },
  longitudinalCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  }
};

export const fullReconPointMap = {
  ravel: {
    0: 0,
    1: -1,
    2: -1,
    3: -1,
  },
  bleedFlush: {
    0: 0,
    1: -1,
    2: 1,
    3: 2,
  },
  rutting: {
    0: 0,
    1: -1,
    2: 2,
    3: 2,
  },
  patching: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  },
  alligatorCracking: {
    0: 0,
    1: 1,
    2: 1,
    3: 2,
  },
  blockCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  },
  transverseCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  },
  longitudinalCracking: {
    0: 0,
    1: -1,
    2: -1,
    3: 1,
  }
};


export const pointMaps = {
  chipCape: chipCapePointMap,
  thinHMA: thinHMAPointMap,
  fullDepth: fullDepthPointMap,
  fullRecon: fullReconPointMap
};
